<script setup>
import dayjs from "dayjs";
import {trans} from "laravel-vue-i18n";
import {usePage} from "@inertiajs/vue3";
import {ref} from "vue";
import {getArrSortsReview} from "@/constants.js";
import MaskIcon from "@/Components/MaskIcon.vue";
import DropdownLink from "@/Components/DropdownLink.vue";
import Card from "@/Components/Card.vue";
import Dropdown from "@/Components/Dropdown.vue";
import InputLabel from "@/Components/InputLabel.vue";
import Badge from "@/Components/Badge.vue";
import StarReview from "@/Components/Icons/StarReview.vue";
import NavLink from "@/Components/NavLink.vue";
import {route} from "ziggy-js";
import {storeToRefs} from "pinia";
import {useUser} from "@/Store/User.js";

defineEmits(['setSelectedFilter'])
const props = defineProps({
    selectedFilter: Object,
})

const page = usePage(),
    user = page.props.user,
    reviewsOrOffers = ref(route().current('users.show.offers') ? 'offers' : 'review'),
    reviewsCount = ref(page.props.reviews.total),
    lotsCount = ref(page.props.lots.total)

const {getUserStatusByIdOrStatus} = storeToRefs(useUser())
const isOnline = () => getUserStatusByIdOrStatus.value(user.id, user.is_online)

const getStyleStarReview = (rating) => {
    if (typeof rating === 'number' || typeof rating === 'string') {
        return rating <= 2 ? 'fill-red-500' : rating <= 3 ? 'fill-orange-500' : rating <= 4 ? 'fill-yellow-400' : 'fill-green-500'
    } else {
        return 'fill-transparent stroke-slate-500 dark:text-slate-400'
    }
}

const lastActivity = () => {
    const now = dayjs(),
        diffMinute = now.diff(user.last_active, 'minute')
    if(diffMinute < 60) {
        return trans('was online :minute minute(s) ago', {minute: diffMinute.toString()})
    } else {
        const diffHour = now.diff(user.last_active, 'hour')
        if(diffHour < 24) {
            return trans('was online :hour hour(s) ago', {hour: diffHour.toString()})
        } else {
            const diffDay = now.diff(user.last_active, 'day')
            if(diffDay <= 14) {
                return trans('was online :day day(s) ago', {day: diffDay.toString()})
            }
        }
    }
    return trans('was online more than 2 weeks ago')
}
</script>
<template>
    <Card class="dark:bg-dark-blue lg:px-3 gap-3 flex">
        <div class="grid grid-cols-1 dark:bg-dark-blue lg:grid-cols-2 lg:gap-3 w-full">
            <div class="cols-span-1 flex gap-5 px-3 lg:px-0 py-3 items-center justify-between">
                <div class="flex gap-2.5 ">
                    <div
                        class="rounded-full border border-gray-400 shadow-md-1 overflow-hidden bg-slate-300 dark:bg-slate-700 shrink-0">
                        <img class="w-full h-full" style="width: 60px; height: 60px" :src="user.profile_photo_url" :alt="user.name">
                    </div>
                    <div>
                        <div class="flex gap-0.75">
                            <div class="px-1 py-0.5 leading-full">
                                <p class="text-black dark:text-slate-200 font-condensed leading-full align-top text-sm line-clamp-1 break-all tracking-wide">
                                    {{
                                        user.name
                                    }}</p>
                            </div>
                            <Badge class="shrink-0" :value="isOnline() ? 'Online' : 'Offline'"
                                   :type="isOnline() ? 'success': 'danger'"/>
                        </div>
                        <div
                            class="px-1 text-xs font-medium font-condensed tracking-wide leading-full text-slate-500 dark:text-slate-400">
                            {{ user.on_site_desc }}
                        </div>
                        <div v-if="!isOnline()"
                             class="px-1 text-xs font-normal font-condensed tracking-wide leading-full text-slate-500 dark:text-slate-400">
                            {{ lastActivity() }}
                        </div>
                    </div>
                </div>
                <div class="relative">
                    <StarReview :class="getStyleStarReview(user.rating)" class="w-[3.71738rem] h-[3.56013rem]"/>
                    <div
                        class="leading-full font-condensed absolute top-[55%] left-1/2 -translate-x-1/2 -translate-y-1/2 text-[1.375rem] font-medium text-white dark:text-dark-blue tracking-wider">
                        {{ user.rating }}
                    </div>
                </div>
            </div>
            <div class="cols-span-1 ">
                <div class="pt-3 grid grid-cols-2">
                    <NavLink :href="route('users.show.reviews', {
                        user: user.id
                    })" :class="reviewsOrOffers === 'review' ? 'border-b-[3px] border-slate-500 dark:border-slate-400 dark:text-slate-500' : 'border-b text-slate-500 dark:text-slate-700 border-white dark:border-dark-blue'"
                         class="cols-span-1  lg:border-slate-500 dark:lg:border-slate-400 cursor-pointer lg:hover:text-slate-700 dark:hover:text-slate-500 hover:lg:border-slate-700 transition-colors">
                        <div
                            class="text-center py-2 text-sm leading-full font-medium tracking-wider uppercase ">
                            {{ $t('Feedbacks') }}
                        </div>
                    </NavLink>
                    <NavLink :href="route('users.show.offers', {
                        user: user.id
                    })" :class="reviewsOrOffers === 'offers' ? 'border-b-[3px] border-slate-500 dark:border-slate-400 dark:text-slate-500' : 'border-b text-slate-500 dark:text-slate-700 border-white dark:border-dark-blue'"
                         class="cols-span-1 lg:border-slate-500 dark:lg:border-slate-400 cursor-pointer dark:hover:text-slate-500 hover:text-slate-700 hover:lg:border-slate-700 transition-colors">
                        <div class="text-center py-2 text-sm leading-full font-medium tracking-wider uppercase">
                            {{$t('Offers')}}
                        </div>
                    </NavLink>
                </div>
                <div
                    class="py-3 lg:py-2 px-3 lg:px-0 flex gap-5 justify-between lg:justify-end bg-white dark:bg-dark-blue rounded-b-lg lg:bg-inherit">
                    <InputLabel
                        :value="`${$t('Total')} ` + (reviewsOrOffers === 'offers' ? lotsCount : reviewsCount)"
                        class="text-sm font-medium leading-120 text-black dark:text-slate-200 flex items-center px-2 uppercase"
                    />
                    <Dropdown
                        class="border border-slate-300 dark:border-slate-700 p-1.5 w-[12.5rem] bg-white dark:bg-dark-blue rounded-lg h-10"
                        align="left">
                        <template #trigger>
                <span class="flex rounded-md items-center justify-between">
                    <button type="button"
                            class="text-sm leading-full dark:text-slate-200 font-normal transition ease-in-out whitespace-nowrap my-0">
                        {{ selectedFilter.title ? selectedFilter.title : selectedFilter.val }}
                    </button>
       <MaskIcon src="/assets/icons/arrow.svg" class="w-6 h-6 bg-[#21272A] dark:bg-white"/>
                </span>
                        </template>
                        <template #content>
                            <div class="">
                                <template v-for="rating in getArrSortsReview()">
                                    <dropdown-link as="button"
                                                   @click="$emit('setSelectedFilter', rating)"
                                                   class="border-b border-dashed border-slate-300 dark:border-slate-700 font-medium text-base">
                                        <template v-if="rating.title">
                                            {{ rating.title }}
                                        </template>
                                        <div class="flex p-1 gap-1" v-else>
                                            <template v-for="value in rating.val">
                                                <StarReview :class="getStyleStarReview(rating.val)"
                                                            class="w-2.5 h-2.5"/>
                                            </template>
                                        </div>
                                    </dropdown-link>
                                </template>
                            </div>
                        </template>
                    </Dropdown>
                </div>
            </div>
        </div>
    </Card>
</template>
